import { Helmet } from 'react-helmet'

import { Cap, Demo, PhonePreview } from '../../primitives'
import { PrototypeFrame } from '../../PrototypeFrame'
import { CLAIM_ROWS, ClaimOutcome, ClaimStatus, NO_CLAIMS } from './claimsData'
import { ClaimsList } from './ClaimsList'
import { ClaimTracker } from './ClaimTracker'

/** How much of a screen exists today — the enum having a value is not the same as code setting it. */
enum Reality {
  LIVE = 'LIVE',
  DECLARED = 'DECLARED',
  ABSENT = 'ABSENT',
}

const REALITY_LABELS: Record<Reality, string> = {
  [Reality.LIVE]: 'Set today',
  [Reality.DECLARED]: 'Declared, never set',
  [Reality.ABSENT]: 'Not modelled',
}

interface ScreenDemo {
  key: string
  label: string
  reality: Reality
  trigger: string
  frameHeight: number
  status?: ClaimStatus
  outcome?: ClaimOutcome
  needsYou?: boolean
}

const LIST_FRAME_HEIGHT = 900
const EMPTY_FRAME_HEIGHT = 360
const TRACKER_FRAME_HEIGHT = 1180

const SCREENS: ScreenDemo[] = [
  {
    key: 'list',
    label: 'Your claims — every claim on the account',
    reality: Reality.ABSENT,
    trigger: 'no customer-facing list exists; ClaimsTable is the staff view and shows raw statuses',
    frameHeight: LIST_FRAME_HEIGHT,
  },
  {
    key: 'list-empty',
    label: 'Your claims — nothing filed yet',
    reality: Reality.ABSENT,
    trigger: 'the state most accounts sit in',
    frameHeight: EMPTY_FRAME_HEIGHT,
  },
  {
    key: ClaimStatus.IN_REVIEW,
    label: 'Claim tracker — in review',
    reality: Reality.DECLARED,
    trigger: 'IN_REVIEW exists in Claim.Status, but no code path ever sets it',
    frameHeight: TRACKER_FRAME_HEIGHT,
    status: ClaimStatus.IN_REVIEW,
  },
  {
    key: ClaimStatus.OPEN_WITH_CARRIER,
    label: 'Claim tracker — open with carrier',
    reality: Reality.ABSENT,
    trigger: 'FILED is declared but never set either; LossRunClaim reports only OPEN/CLOSED',
    frameHeight: TRACKER_FRAME_HEIGHT,
    status: ClaimStatus.OPEN_WITH_CARRIER,
  },
  {
    key: 'needs-you',
    label: 'Claim tracker — blocked on you',
    reality: Reality.DECLARED,
    trigger: 'INFO_NEEDED is declared and never set; nothing records what was asked for',
    frameHeight: TRACKER_FRAME_HEIGHT,
    status: ClaimStatus.OPEN_WITH_CARRIER,
    needsYou: true,
  },
  {
    key: 'closed-paid',
    label: 'Claim tracker — closed, paid',
    reality: Reality.ABSENT,
    trigger: 'no closed state and no paid_loss on Claim; the figures here come from LossRunClaim',
    frameHeight: TRACKER_FRAME_HEIGHT,
    status: ClaimStatus.CLOSED,
    outcome: ClaimOutcome.PAID,
  },
  {
    key: 'closed-denied',
    label: 'Claim tracker — closed, denied',
    reality: Reality.ABSENT,
    trigger: 'a denied claim would read exactly like a paid one — neither status is reachable',
    frameHeight: TRACKER_FRAME_HEIGHT,
    status: ClaimStatus.CLOSED,
    outcome: ClaimOutcome.DENIED,
  },
]

const BRIEF = (
  <div className="border-ink-700 grid grid-cols-1 gap-6 border-t pt-6 sm:grid-cols-2 lg:grid-cols-4">
    <div>
      <Cap onDark>Goal</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Give a customer one place to see where a claim stands, so they stop calling their broker to ask. One
        status runs the whole length of the claim; who is holding it, and whether you are blocked, are read
        off that status rather than tracked beside it. Metric: share of claims where the customer contacts ops
        for a status update (baseline TBD).
      </p>
    </div>

    <div>
      <Cap onDark>Project: Claims visibility</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        A claim is the moment a customer finds out what they actually bought. Today they file it and then hear
        nothing until someone chases it by hand, which is the worst possible silence.
      </p>
    </div>

    <div>
      <Cap onDark>What is real</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Five statuses, one per stage. <code>SUBMITTED</code> is folded into <code>IN_REVIEW</code> on purpose:
        submitting is the same instant a claim lands in review, and a customer cannot act on the difference.
        Behind that, only <code>DRAFT</code> and <code>SUBMITTED</code> are ever set by code.{' '}
        <code>submit_claim()</code> writes SUBMITTED against a signature; <code>IN_REVIEW</code>,{' '}
        <code>INFO_NEEDED</code> and <code>FILED</code> are declared in the enum and written by nothing, and{' '}
        <code>ClaimBrokerReview</code> is never created either. The carrier half stops at two stages because{' '}
        <code>LossRunClaim.Status</code> is OPEN/CLOSED/UNKNOWN, so no feed can drive anything finer. Steps
        marked <em>relayed</em> need a person to type them in.
      </p>
    </div>

    <div>
      <Cap onDark>Feedback</Cap>
      <ul className="text-ink-200 mt-2 flex flex-col gap-1 text-xs leading-relaxed">
        <li>
          Slack channel:{' '}
          <a
            href="https://cailabsinc.slack.com/archives/C0C3V5F5LMP"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
          >
            #test-claims-v1
          </a>
        </li>
      </ul>
    </div>

    <div>
      <Cap onDark>Next</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        First make one declared status real: give staff a way to mark a claim reviewed, so{' '}
        <code>IN_REVIEW</code> gets written and the queue-versus-being-worked split becomes measurable. Then
        decide whether Cai stores a carrier claim number and an adjuster, or keeps pointing at the loss run,
        and add the outcome fields so a denied claim stops reading like a paid one.
      </p>
    </div>
  </div>
)

export function ClaimsPrototypePage() {
  return (
    <PrototypeFrame
      title="Claims V1.0"
      owner="Yang Qu"
      date="September 15, 2026"
      status="Front-end prototype"
      brief={BRIEF}
    >
      <Helmet>
        <title>Prototype: Claims V1.0</title>
      </Helmet>
      <div className="flex flex-col gap-10">
        {SCREENS.map((screen) => (
          <Demo key={screen.key} label={screen.label}>
            {screen.status ? (
              <ClaimTracker status={screen.status} outcome={screen.outcome} needsYou={screen.needsYou} />
            ) : (
              <ClaimsList claims={screen.key === 'list-empty' ? NO_CLAIMS : CLAIM_ROWS} />
            )}
            <div className="text-ink-400 mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px]">
              <Cap>Claim.Status</Cap>
              {screen.reality === Reality.LIVE ? (
                <span className="bg-ink-100 text-ink-700 rounded-sm px-2 py-0.5 text-[10px] font-medium">
                  {REALITY_LABELS[screen.reality]}
                </span>
              ) : (
                <span className="border-ink-300 text-ink-500 rounded-sm border border-dashed px-2 py-0.5 text-[10px] font-medium">
                  {REALITY_LABELS[screen.reality]}
                </span>
              )}
              <span>{screen.trigger}</span>
            </div>
            <div className="mt-6">
              <Cap>Mobile · 375px</Cap>
              <div className="mt-3">
                <PhonePreview
                  src={`/design-system/frames/claim?screen=${screen.key}`}
                  title={`${screen.label} at 375px`}
                  height={screen.frameHeight}
                  autoHeight
                />
              </div>
            </div>
          </Demo>
        ))}
      </div>
    </PrototypeFrame>
  )
}
